import { useState } from 'react';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import {
  useGetAllEquipments,
  useUpdateManyEquipments,
} from '@/hooks/equipment.hooks';
import { useNavigate } from 'react-router-dom';
import { EquipmentTableView } from '../components/EquipmentTableView';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { EquipmentTableHeaders } from '../components/EquipmentTableHeaders';
import { EquipmentTableData } from '../components/EquipmentTableData';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { WarningMessage } from '@/components/Message/WarningMessage';

export const EquipmentListPage = () => {
  const { t } = useTranslation('equipment');
  const navigate = useNavigate();
  const [totalActive, setTotalActive] = useState<number>(0);

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TEquipmentSchema>({
    t,
    useUpdateData: useUpdateManyEquipments,
  });
  /**
   * === END OF Deactivate Data section ===
   */

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };

  return (
    <>
      <PageHeader
        title={t('common:equipments')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading text={t('common:detailsRelatedToCurrentSection')} />
        }
        actionListElement={
          <>
            <HeadingButton
              text={t('common:importAList')}
              leftIcon={
                <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
              }
              onClick={() => navigate('#')}
            />
            <HeadingButton
              text={t('addEquipment')}
              leftIcon={
                <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
              }
              onClick={() => navigate('/equipment/create')}
            />
          </>
        }
      />
      <div>
        <EquipmentTableView<TEquipmentSchema>
          t={t}
          useGetAllData={useGetAllEquipments}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={EquipmentTableHeaders}
          tableData={EquipmentTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
        />
      </div>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage message={t('common:unableToDeactivateLinkedItem')} />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans i18nKey="common:confirmDeactivateItems" />
          ) : (
            t('common:confirmDeactivateItem')
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
    </>
  );
};
